import db from "@/db";
import LINES from "@/db/enums/lines";

import {
  SAMPLE_FLASK_TYPES,
  SAMPLE_FLASK_TYPES_SHORT_NAMES,
} from "@/db/enums/sampleFlaskTypes";
import { v4 as uuidv4 } from "uuid";
import PARAMETERS_CATEGORIES from "@/db/enums/parametersCategories";
import { METAL_NAMES } from "@/db/enums/metalParameterNames.js";
import { hasAcidityAlkalinityParameter } from "@/db/enums/acidityAlkalinityParameters";

export default function useSampleFlaskGenerator(i18n) {
  let metal_particule_uuid = "";
  let metal_gaz_uuid = "";
  const messages = {
    en: {
      LIGNE_PRINCIPALE: "Main line",
      LIGNE_SECONDAIRE: "Secondary line",
      FILTRE: "Filter",
      BARBOTAGE: "Bubbling",
      RENDEMENT: "Yield",
      RINCAGE: "Rinse",
      BLANC_FILTRAGE: "Blank filtering",
      BLANC_BARBOTAGE: "Blank bubbling",
      BLANC_RINCAGE: "Blank rinse",
      AUTRE_SUPPORT: "Other supports",
    },
    fr: {
      LIGNE_PRINCIPALE: "Ligne principale",
      LIGNE_SECONDAIRE: "Ligne secondaire",
      FILTRE: "Filtre",
      BARBOTAGE: "Barbotage",
      RENDEMENT: "Rendement",
      RINCAGE: "Rincage",
      BLANC_FILTRAGE: "Blanc de filtrage",
      BLANC_BARBOTAGE: "Blanc de barbotage",
      BLANC_RINCAGE: "Blanc de rinçage",
      AUTRE_SUPPORT: "Autres supports",
    },
  };

  if (i18n) {
    i18n.mergeLocaleMessage("fr", messages.fr);
    i18n.mergeLocaleMessage("en", messages.en);
  }

  /*
    Logique infernale pour choisir une analyse.

    on récupérer toute les analyses qui ont les paramètres des échantillons.

    On les trie par taille, car il faut essayer de prendre les analyse qui ont le plus de paramètre possible
    (ca coute moins cher).

  */
  const getAnalysis = async (uuidsList, sampleFlaskType) => {
    if (!uuidsList.length) return [];
    let analysisCandidates = await db.analysis
      .filter(({ parameters, supportList }) => {
        return (
          uuidsList.some((uuid) => parameters.includes(uuid)) &&
          parameters.every((uuid) => uuidsList.includes(uuid)) &&
          supportList.includes(sampleFlaskType)
        );
      })
      .toArray();

    analysisCandidates = analysisCandidates.sort(
      (a, b) => b.parameters.length - a.parameters.length
    );

    const selectedAnalysis = [];
    const foundParameters = [];
    const analysisWithManyParameter = analysisCandidates.filter(
      (a) => a.parameters.length > 1
    );

    for (const analysisMetal of analysisWithManyParameter) {
      // si certains paramètre de cette analyse sont déjà dans un plus gros pack, on skip
      if (
        foundParameters.some((parameter) =>
          analysisMetal.parameters.includes(parameter)
        )
      )
        continue;

      // on garde les paramètre qu'on a dans des pack, et on choisis l'analyse
      foundParameters.push(...analysisMetal.parameters);
      selectedAnalysis.push(analysisMetal);
    }

    // cas pour les analyse avec 1 paramètres. Ces analyse ont plusieurs candidats
    const analysisWithOneParameter = analysisCandidates.filter(
      (a) => a.parameters.length === 1
    );
    const mapParameterWIthTheirAnalysis = new Map();

    // on va mettre dans une map l'uuid du paramètre et les analyse éligible
    for (const soloAnalysis of analysisWithOneParameter) {
      const parameterUuid = soloAnalysis.parameters[0];
      // si il est déjà dans un plus gros pack, on skip
      if (foundParameters.includes(parameterUuid)) continue;
      if (!mapParameterWIthTheirAnalysis.has(parameterUuid)) {
        mapParameterWIthTheirAnalysis.set(parameterUuid, []);
      }

      mapParameterWIthTheirAnalysis.get(parameterUuid).push(soloAnalysis);
    }
    // ensuite on regarde les analyse candidate d'un paramètre, si l'un d'elle est l'analyse par défaut, on la prend
    // si aucun, on prend au pif
    // si ya qu'un seul candidat, on prend l'unique
    for (const values of mapParameterWIthTheirAnalysis.values()) {
      if (values.length === 1) selectedAnalysis.push(values[0]);
      else {
        const parDefaut = values.filter((v) => v.isDefault);
        if (parDefaut.length === 1) selectedAnalysis.push(parDefaut[0]);
        else selectedAnalysis.push(values[0]);
      }
    }

    // salle affaire
    return selectedAnalysis;
  };

  const getSampleFlaskAnalysisData = async (
    parameterInSampleUuids,
    metalPartInSums,
    metalGazInSums,
    sampleFlaskType
  ) => {
    const analysis = await getAnalysis(
      [...parameterInSampleUuids, ...metalPartInSums, ...metalGazInSums],
      sampleFlaskType
    );

    return {
      reference: analysis[0]?.catalogReference,
      analysis: analysis,
    };
  };

  const sampleToFlaskGenerator = (sample, installation, phase) => {
    const mainParameters = sample.mainParametersObjects;
    const firstParameter = mainParameters[0];
    const isLP = firstParameter.line == LINES.LIGNE_PRINCIPALE;

    const isMakingYield = mainParameters.some((parameter) => {
      return parameter.makingYield;
    });

    const hasMarker = mainParameters.some((parameter) => parameter.hasMarker);

    return {
      uuid: uuidv4(),
      installation: installation?.uuid,
      phase: phase?.uuid,
      sample: sample.uuid,
      sampleParametersName: "",
      phaseOrder: phase?.order,
      fullParametersNames: "",
      tries: 1,
      sampleNumbers: phase?.order,
      FILTRE: isLP ? 1 : 0,
      BARBOTAGE: isLP ? 0 : 1,
      AUTRE_SUPPORT: hasMarker && phase?.order === 1 ? 1 : 0,
      RENDEMENT: isMakingYield ? 1 : 0,
      RINCAGE: 0,
      BLANC_FILTRAGE: 0,
      BLANC_BARBOTAGE: 0,
      BLANC_RINCAGE: 0,
    };
  };

  const listMetalFromSum = async (installation, suffixFilter) => {
    const sums = await db.sums
      .where("installation")
      .equals(installation.uuid)
      .toArray();
    let parameters = [];
    for (const sum of sums) {
      await sum.setRelationships();
      const metalparametersInSome = sum.parameters.filter(
        (p) =>
          p.categories.includes(PARAMETERS_CATEGORIES.MTX) &&
          p.name.endsWith(suffixFilter)
      );
      parameters.push(...metalparametersInSome);
    }
    return [...new Set(parameters.map(({ uuid }) => uuid))];
  };
  const flaskPrepToSampleFlask = async (installation, sampleFlaskPrep) => {
    await sampleFlaskPrep.setRelationships();
    await sampleFlaskPrep.sampleObject.setRelationships();
    const isYieldLocked = hasAcidityAlkalinityParameter(
      sampleFlaskPrep.sampleObject.mainParametersObjects
    );
    const sampleFlasks = [];
    for (let sampleFlaskType of Object.values(SAMPLE_FLASK_TYPES)) {
      const isRendementForced =
        sampleFlaskType === SAMPLE_FLASK_TYPES.RENDEMENT && isYieldLocked;
      if (!sampleFlaskPrep[sampleFlaskType] && !isRendementForced) continue;
      const parameterNames = sampleFlaskPrep.sampleObject.mainParametersObjects
        .map((parameter) => parameter.sampleShortName)
        .join("");
      const parametersIds = [...sampleFlaskPrep.sampleObject.mainParameters];
      let metalPartInSums = [];
      let metalGazInSums = [];
      if (parametersIds.includes(metal_particule_uuid)) {
        metalPartInSums = await listMetalFromSum(installation, "part.");
      }
      if (parametersIds.includes(metal_gaz_uuid)) {
        metalGazInSums = await listMetalFromSum(installation, "gaz.");
      }

      const { reference, analysis } = await getSampleFlaskAnalysisData(
        parametersIds,
        metalPartInSums,
        metalGazInSums,
        sampleFlaskType
      );

      const paddedId = ("00000" + installation.projectObject.readableId).slice(
        -5
      );
      const flaskName = `${paddedId}_${installation.sampleBaseName}_P${sampleFlaskPrep.phaseOrder}_${parameterNames}_${SAMPLE_FLASK_TYPES_SHORT_NAMES[sampleFlaskType]}`;
      const newFlask = {
        uuid: uuidv4(),
        installation: installation.uuid,
        name: flaskName,
        type: sampleFlaskType,
        samples: [sampleFlaskPrep.sample],
        tryNumber: `${i18n.t(sampleFlaskType)} Phase ${sampleFlaskPrep.phaseOrder}`,
        reference: reference,
        analysisCodes: filerAnalysisCodesBySupport(analysis, sampleFlaskType),
      };
      sampleFlasks.push(newFlask);
    }
    return sampleFlasks;
  };

  const buildSampleFlasks = async (installationUuid) => {
    const installation = await db.installations.get({ uuid: installationUuid });
    const MTX_PART = await db.parameters.get({ name: METAL_NAMES.MTX_PART });
    metal_particule_uuid = MTX_PART.uuid;
    const MTX_GAZ = await db.parameters.get({ name: METAL_NAMES.MTX_GAZ });
    metal_gaz_uuid = MTX_GAZ.uuid;
    const sampleFlasks = [];
    await installation.setRelationships();
    for (const phase of installation.phases) {
      const sampleFlaskPreps = await db.sampleFlaskPreps
        .where("phase")
        .equals(phase.uuid)
        .toArray();
      for (const sampleFlaskPrep of sampleFlaskPreps) {
        const subSampleFlasks = await flaskPrepToSampleFlask(
          installation,
          sampleFlaskPrep
        );
        sampleFlasks.push(...subSampleFlasks);
      }
    }
    let autreSupportKept = false;
    return sampleFlasks.filter((flask) => {
      if (flask.type !== SAMPLE_FLASK_TYPES.AUTRE_SUPPORT) return true;
      if (autreSupportKept) return false;
      autreSupportKept = true;
      return true;
    });
  };

  const buildMissingSampleFlasks = async (installationUuid) => {
    const existingFlasks = await db.sampleFlasks
      .where("installation")
      .equals(installationUuid)
      .toArray();
    const existingKeys = new Set(
      existingFlasks.flatMap(({ samples, type }) =>
        (samples || []).map((sample) => `${sample}|${type}`)
      )
    );
    const hasAutreSupport = existingFlasks.some(
      ({ type }) => type === SAMPLE_FLASK_TYPES.AUTRE_SUPPORT
    );

    const sampleFlasks = await buildSampleFlasks(installationUuid);
    return sampleFlasks.filter(({ samples, type }) => {
      if (type === SAMPLE_FLASK_TYPES.AUTRE_SUPPORT) return !hasAutreSupport;
      return !samples.some((sample) => existingKeys.has(`${sample}|${type}`));
    });
  };

  const filerAnalysisCodesBySupport = (analysis, support) => {
    return analysis
      .filter((a) => a.supportList.includes(support))
      .map(({ code }) => code);
  };

  return {
    sampleToFlaskGenerator,
    buildSampleFlasks,
    buildMissingSampleFlasks,
  };
}
